import { formatStatusLabel } from '@/ui/primitives/statusChips';
import { metricsStatusOrder } from '@flowqa/shared';
import type {
  BuildStatus,
  FeatureDto,
  ReleaseDetailDto,
  SuiteRunDto,
  TestRunDto,
} from '@flowqa/shared';

export const featureStatusOptions: Array<{ value: FeatureDto['status']; label: string }> = [
  { value: 'draft', label: formatStatusLabel('draft') },
  { value: 'planned', label: formatStatusLabel('planned') },
  { value: 'ready', label: formatStatusLabel('ready') },
  { value: 'released', label: formatStatusLabel('released') },
];

export const releaseStatusOptions: Array<{ value: ReleaseDetailDto['status']; label: string }> = [
  { value: 'draft', label: formatStatusLabel('draft') },
  { value: 'planned', label: formatStatusLabel('planned') },
  { value: 'ready', label: formatStatusLabel('ready') },
  { value: 'released', label: formatStatusLabel('released') },
  { value: 'overdue', label: formatStatusLabel('overdue') },
];

export const buildStatusOptions: Array<{ value: BuildStatus; label: string }> = [
  { value: 'completed', label: formatStatusLabel('completed') },
  { value: 'failed', label: formatStatusLabel('failed') },
  { value: 'running', label: formatStatusLabel('running') },
  { value: 'queued', label: formatStatusLabel('queued') },
];

const suiteRunStatusOrder: Array<SuiteRunDto['status']> = [
  ...(['passed', 'failed', 'skipped', 'errored', 'untested'] as const).filter((status) =>
    metricsStatusOrder.includes(status),
  ),
];

export const suiteRunStatusOptions: Array<{
  value: SuiteRunDto['status'];
  label: string;
}> = suiteRunStatusOrder.map((status) => ({
  value: status,
  label: formatStatusLabel(status),
}));

const suiteRunSummaryStatusOrder: Array<SuiteRunDto['status']> = [
  'passed',
  'failed',
  'skipped',
  'errored',
];

export const suiteRunSummaryStatusOptions: Array<{
  value: SuiteRunDto['status'];
  label: string;
}> = suiteRunSummaryStatusOrder.map((status) => ({
  value: status,
  label: formatStatusLabel(status),
}));

const testRunStatusOrder: Array<TestRunDto['status']> = [
  ...(['passed', 'failed', 'skipped', 'errored', 'untested'] as const).filter((status) =>
    metricsStatusOrder.includes(status),
  ),
];

export const testRunStatusOptions: Array<{
  value: TestRunDto['status'];
  label: string;
}> = testRunStatusOrder.map((status) => ({
  value: status,
  label: formatStatusLabel(status),
}));

const testRunSummaryStatusOrder: Array<TestRunDto['status']> = [
  'passed',
  'failed',
  'skipped',
  'errored',
];

export const testRunSummaryStatusOptions: Array<{
  value: TestRunDto['status'];
  label: string;
}> = testRunSummaryStatusOrder.map((status) => ({
  value: status,
  label: formatStatusLabel(status),
}));

export type ReleaseAutomationFlag =
  | 'automateReleaseStatuses'
  | 'requireFeaturesForReady'
  | 'requireReadyFeaturesForReady';

export const releaseAutomationLabels: Record<
  ReleaseAutomationFlag,
  { label: string; helperText: string }
> = {
  automateReleaseStatuses: {
    label: 'Automate Release Status',
    helperText:
      'Automatically derive release status from planned/released dates and feature readiness.',
  },
  requireFeaturesForReady: {
    label: 'Require Features',
    helperText: 'Releases cannot be marked as ready unless they include at least one feature.',
  },
  requireReadyFeaturesForReady: {
    label: 'Require All Features Ready',
    helperText:
      'When enabled, all linked features must be ready before the release can be marked as ready.',
  },
};
